import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Flex, Typography } from 'antd';
import { useEffect, useState } from 'react';

import AppModal from '@components/app-modal';
import { useAcceptLegal } from '@lib/api/hooks/legal/use-accept-legal';
import { legalKey, legalStatusOptions } from '@lib/api/options/legal';
import { ROUTE_PATHS } from '@modules/route-paths';

interface Props {
  accountId: string;
}

const LegalAcceptance = ({ accountId }: Props) => {
  const client = useQueryClient();
  const query = useQuery(legalStatusOptions(accountId));
  const { acceptLegal, accepting } = useAcceptLegal(accountId);
  const [deferredRevision, setDeferredRevision] = useState<string>();
  const [forced, setForced] = useState(false);
  const state = query.data;
  useEffect(() => {
    const refresh = () => {
      client.invalidateQueries({ queryKey: legalKey }).catch(() => undefined);
    };

    const storage = (event: StorageEvent) => {
      if (event.key === 'nexo:legal-update') {
        refresh();
      }
    };

    const requireAcceptance = () => {
      setForced(true);
      refresh();
    };

    window.addEventListener('storage', storage);
    window.addEventListener('nexo:legal-required', requireAcceptance);
    return () => {
      window.removeEventListener('storage', storage);
      window.removeEventListener('nexo:legal-required', requireAcceptance);
    };
  }, [client]);
  if (query.isError) {
    return (
      <Alert
        type="warning"
        title="Não foi possível verificar a atualização dos termos."
        action={
          <Button onClick={() => query.refetch().catch(() => undefined)}>
            Tentar novamente
          </Button>
        }
      />
    );
  }

  if (!state?.requiresAcceptance || !state.acceptanceRevision) {
    return null;
  }

  const revision = state.acceptanceRevision;
  const open = forced || deferredRevision !== revision;
  const defer = () => {
    setDeferredRevision(revision);
    setForced(false);
  };

  const onAccept = () => {
    acceptLegal(revision).catch(() => undefined);
  };

  const suffix = `?revision=${encodeURIComponent(state.document.id)}`;
  return (
    <>
      <Alert
        type="warning"
        title={
          state.restricted
            ? 'Aceite os termos atualizados para iniciar novas operações comerciais.'
            : 'Os Termos de Uso foram atualizados.'
        }
        action={<Button onClick={() => setForced(true)}>Revisar termos</Button>}
      />
      <AppModal
        title="Atualização dos Termos de Uso"
        open={open}
        blocking={accepting}
        onCancel={defer}
        footer={
          <Flex gap="small" wrap justify="flex-end">
            <Button disabled={accepting} onClick={defer}>
              Agora não
            </Button>
            <Button type="primary" loading={accepting} onClick={onAccept}>
              Li e aceito os Termos de Uso
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap="middle">
          <Typography.Text>
            Versão {state.document.documentVersion} · Vigência:{' '}
            {new Date(state.effectiveAt).toLocaleString('pt-BR', {
              timeZone: 'America/Maceio',
            })}
          </Typography.Text>
          <ul>
            {state.document.summary.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
          <Typography.Paragraph>
            As regras de saldo são específicas dos lojistas. Os limites
            financeiros terão ativação comunicada separadamente.
          </Typography.Paragraph>
          <Typography.Paragraph>
            Para cadastrar uma nova loja, o aceite atualizado já é necessário.
            Após a vigência, o aceite será necessário para novas operações
            comerciais. Saques, suporte e cumprimento de pedidos existentes
            permanecem acessíveis.
          </Typography.Paragraph>
          <a
            href={ROUTE_PATHS.TERMS_OF_USE + suffix}
            target="_blank"
            rel="noreferrer"
          >
            Ler os Termos de Uso completos
          </a>
          <a
            href={ROUTE_PATHS.PRIVACY_POLICY + suffix}
            target="_blank"
            rel="noreferrer"
          >
            Consultar a Política de Privacidade
          </a>
          <Typography.Text type="secondary">
            A Política de Privacidade é apresentada para ciência. Este aceite
            não representa consentimento genérico para tratamento de dados.
          </Typography.Text>
        </Flex>
      </AppModal>
    </>
  );
};

export default LegalAcceptance;
